import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Users, Check } from "lucide-react";

/**
 * Combobox for attending selection.
 *  - Users can type any name freely (native <input> with datalist-style dropdown).
 *  - Suggestions appear from the fellow's saved staff roster; clicking one fills it.
 *  - An "Add to roster" affordance shows when the typed name is new — so the next
 *    time they can just pick it from the list.
 * Value/onChange work as a controlled string (attending name).
 */
export default function StaffPicker({ value, onChange, required, hideAdd, testId, placeholder }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff");
      setStaff(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const q = (value || "").trim().toLowerCase();
  const matches = q
    ? staff.filter((s) => s.name.toLowerCase().includes(q))
    : staff;
  const inRoster = staff.some((s) => s.name.trim().toLowerCase() === q);
  const canSaveNew = !hideAdd && q.length >= 2 && !inRoster;

  const persistToRoster = async () => {
    const trimmed = (value || "").trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const { data } = await api.post("/staff", { name: trimmed });
      toast.success(`Added ${data.name} to your roster`);
      await load();
    } catch {
      toast.error("Could not add attending");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2" ref={wrapRef}>
      <div className="relative">
        <Users className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neu-muted pointer-events-none" />
        <input
          type="text"
          value={value || ""}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder || (loading ? "Loading staff…" : "Type or pick attending…")}
          className="neu-input pl-10 pr-4"
          required={required}
          data-testid={testId}
          autoComplete="off"
          spellCheck={false}
        />
        {open && (matches.length > 0 || canSaveNew) && (
          <div
            className="absolute z-30 mt-2 left-0 right-0 max-h-64 overflow-y-auto rounded-xl bg-white shadow-neu border border-neu-muted/20"
            data-testid={`${testId}-suggestions`}
          >
            {matches.map((s) => {
              const isSelected = s.name.trim().toLowerCase() === q;
              return (
                <button
                  type="button"
                  key={s.id || s.name}
                  onClick={() => {
                    onChange(s.name);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 hover:bg-neu-bg transition-colors border-b border-neu-muted/10 last:border-b-0 flex items-center justify-between text-sm ${
                    isSelected ? "bg-neu-bg" : ""
                  }`}
                  data-testid={`${testId}-suggestion-${s.name}`}
                >
                  <span>{s.name}</span>
                  {isSelected && <Check className="w-3.5 h-3.5 text-neu-accent" />}
                </button>
              );
            })}
            {canSaveNew && (
              <button
                type="button"
                onClick={persistToRoster}
                disabled={saving}
                className="w-full text-left px-3 py-2 hover:bg-neu-bg border-t border-neu-muted/20 flex items-center gap-2 text-sm text-neu-accent"
                data-testid={`${testId}-add-current`}
              >
                <Plus className="w-4 h-4" />
                {saving ? "Adding…" : `Add "${value}" to my roster`}
              </button>
            )}
          </div>
        )}
      </div>
      {!loading && staff.length === 0 && (
        <div className="text-xs subtle">
          Roster is empty. Type a name; you'll be able to save it for next time.
        </div>
      )}
    </div>
  );
}
